import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRightIcon, ChartNoAxesCombinedIcon, ExternalLinkIcon, XIcon } from 'lucide-react';
const IMPACT_MEASUREMENT_URL = 'https://mtg.snakker.io';
export function ImpactMeasurementButton() {
  const [isOpen, setIsOpen] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);
  return <>
      <button type="button" onClick={() => setIsOpen(true)} aria-haspopup="dialog" aria-expanded={isOpen} className="hide-on-print absolute bottom-3 left-3 z-50 flex h-9 items-center gap-2 rounded-full border border-[var(--brand)]/15 bg-white/95 px-4 text-xs font-bold text-[var(--brand)] shadow-md backdrop-blur transition hover:-translate-y-0.5 hover:bg-[var(--brand)] hover:text-white hover:shadow-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--brand)]/30" data-id="element-55" data-mp="27fu3kytehro1">
        <ChartNoAxesCombinedIcon className="h-4 w-4" aria-hidden="true" data-id="element-56" data-mp="3o8d18a95xlb0" />
        <span data-id="element-57" data-mp="3s9f1muetg3ek">How to measure impact</span>
        <ArrowUpRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-58" data-mp="3s8dqyr1ppixd" />
      </button>

      <AnimatePresence data-id="element-59" data-mp="12cag2yz9x4c">
        {isOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="hide-on-print fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-[clamp(1rem,3vw,2.5rem)] backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="impact-dialog-title" data-id="element-60" data-mp="11avvoir6shv">
            <button type="button" onClick={() => setIsOpen(false)} className="absolute inset-0 cursor-default" aria-label="Close impact measurement window" data-id="element-61" data-mp="jtvfefckbl90" />

            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 12,
          scale: 0.98
        }} transition={{
          duration: 0.25
        }} className="relative z-10 flex h-full max-h-[92vh] w-full max-w-[1500px] flex-col overflow-hidden rounded-3xl bg-white shadow-2xl" data-id="element-62" data-mp="2pmjmysw03ha7">
              <header className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-black/10 px-5 py-3" data-id="element-63" data-mp="38lfd9t4qnbir">
                <div className="flex items-center gap-3" data-id="element-64" data-mp="34jkf2xi3te97">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-65" data-mp="34hzlpb7j01cx">
                    <ChartNoAxesCombinedIcon className="h-5 w-5" aria-hidden="true" data-id="element-66" data-mp="1a6b0wsem54km" />
                  </span>
                  <div data-id="element-67" data-mp="101mbag0b4y9u">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--muted)]" data-id="element-68" data-mp="102e79rxtgy0n">
                      Interactive framework
                    </p>
                    <h2 id="impact-dialog-title" className="text-base font-extrabold text-[var(--fg)]" data-id="element-69" data-mp="qcumggqjmu35">
                      How to measure impact
                    </h2>
                  </div>
                </div>

                <div className="flex items-center gap-2" data-id="element-70" data-mp="gd3sqh9t4j44">
                  <a href={IMPACT_MEASUREMENT_URL} target="_blank" rel="noopener noreferrer" className="hidden h-9 items-center gap-2 rounded-full px-3 text-xs font-bold text-[var(--brand)] transition hover:bg-[var(--brand)]/10 sm:flex" data-id="element-71" data-mp="lcq6qsxgm3dz">
                    <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-72" data-mp="21sj916zvxr3f" />
                    Open in new tab
                  </a>
                  <button ref={closeButtonRef} type="button" onClick={() => setIsOpen(false)} className="flex h-10 items-center gap-2 rounded-full bg-[var(--brand)] px-4 text-xs font-extrabold text-white shadow-sm transition hover:bg-[var(--brand-dark)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--brand)]/30" aria-label="Close How to measure impact" data-id="element-73" data-mp="26nymhxtzdkzb">
                    <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-74" data-mp="3jwem7sicnnc4" />
                    Close
                  </button>
                </div>
              </header>

              <iframe src={IMPACT_MEASUREMENT_URL} title="How to measure impact" className="min-h-0 w-full flex-1 border-0 bg-white" allow="fullscreen" data-id="element-75" data-mp="3a5juw9q8xxjt" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}